/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx, useTheme } from '@emotion/react';
import { FC, useMemo } from 'react';
import dayjs from 'dayjs';
import { useMiddlewareContext } from '../hooks/useMiddlewareContext';
import { headerComponentContainer, headerContainer, headerContent } from '../styles';
import { ECalendarMode, ECalendarView, IHeaderContainerProps, IResource } from '../types';
import { MonthlyHeaderContainer } from './monthly/MonthlyHeaderContainer';
import { timeBoxWidth } from './styles';
import { TimeLine } from './Timeline';
import { useResources } from '../hooks/useResources';
import { monthlyDatesHeaderContainer } from './styles';
import { IColors } from '../../types';
import { isSameDate } from '@bondsports/date-time';

const todayTextColorCss = (colors: IColors) => css`
	color: ${colors.tx_text_primary};
	font-weight: bold;
`;

export const HeaderContainer: FC<IHeaderContainerProps> = ({
	width,
	headerRef,
	ResourceGroupHeader,
	resourceId,
	groups,
	selectResourceGroup,
	resourceGroups,
	handleSetSelectedMonthlyResource,
	view = ECalendarView.VERTICAL,
}) => {
	const { options } = useMiddlewareContext();
	const { mode } = options || {};
	const horizontal = view === ECalendarView.HORIZONTAL;
	const colors = useTheme();

	const { resources, columnsCount } = useResources({ resourceGroups });

	const today: Date = new Date();

	// Generate days for DayX view
	const days = useMemo(() => {
		if (mode !== ECalendarMode.DAYX || resourceId === undefined) return [];

		const startDate = dayjs(options?.date);
		const daysArray: dayjs.Dayjs[] = [];
		const daysCount = options?.dayXDaysCount || 1;

		for (let i = 0; i < daysCount; i++) {
			daysArray.push(startDate.add(i, 'day'));
		}

		return daysArray;
	}, [mode, resourceId, options?.date, options?.dayXDaysCount]);

	if (mode == ECalendarMode.MONTHLY) {
		let resource: IResource | undefined = resourceId
			? resources.find(item => Number(item.id) === Number(resourceId))
			: undefined;

		if (!resource && resources[0]) {
			resource = resources[0];
		}

		if (resource && resource.children?.length > 0) {
			resource = { ...resource.children[0], children: [] };
		}

		return resource ? (
			<MonthlyHeaderContainer
				width={width}
				headerRef={headerRef}
				ResourceGroupHeader={ResourceGroupHeader}
				resource={resource}
				resources={resources}
				view={view}
				mode={mode}
				groups={groups}
				handleSetSelectedMonthlyResource={handleSetSelectedMonthlyResource}
				resourceId={resourceId}
			/>
		) : null;
	}

	return (
		<div css={headerContainer(width, horizontal)} data-aid="HeaderContainer-atoms">
			<div ref={headerRef} css={headerContent(horizontal)}>
				{horizontal ? (
					<TimeLine width={width + timeBoxWidth} view={view} />
				) : // For DAYX mode with resourceId, show resource header and days (like Monthly view)
				mode === ECalendarMode.DAYX && resourceId !== undefined ? (
					(() => {
						const flattenedResources: IResource[] = resources.flatMap(resource => [
							resource,
							...(resource.children || []),
						]);

						const selectedResource =
							flattenedResources.find(item => Number(item.id) === Number(resourceId)) || resources[0];

						if (!selectedResource) return null;

						return (
							<div
								key={selectedResource.id}
								id={`resource_${selectedResource.id}`}
								css={headerComponentContainer(1, 1, true)}
							>
								<ResourceGroupHeader
									key={`ResourceGroupHeader-${selectedResource.id}`}
									resources={flattenedResources}
									state={selectedResource}
									view={view}
									selectedMonthlyResource={resourceId}
									mode={mode}
									groups={groups}
									selectResourceGroup={selectResourceGroup}
									resourceGroups={resourceGroups}
									handleSetSelectedMonthlyResource={handleSetSelectedMonthlyResource}
								/>
								<div css={monthlyDatesHeaderContainer(colors)}>
									{days.map((day, index) => {
										const isToday: boolean = isSameDate(day, today, 'date');
										const formattedDate: string = day.format('ddd, MMM D');

										return (
											<div css={isToday && todayTextColorCss(colors)} key={formattedDate}>
												{formattedDate}
											</div>
										);
									})}
								</div>
							</div>
						);
					})()
				) : (
					// Regular multi-resource display
					resources.map((resource: IResource) => {
						const children: IResource[] = resource.children ?? [];

						return (
							<div
								key={resource.id}
								id={`resource_${resource.id}`}
								css={headerComponentContainer(children.length || 1, columnsCount)}
							>
								<ResourceGroupHeader
									key={`ResourceGroupHeader-${resource.id}`}
									resources={resources}
									state={resource}
									view={view}
									selectedMonthlyResource={resourceId}
									mode={mode}
									groups={groups}
									selectResourceGroup={selectResourceGroup}
									resourceGroups={resourceGroups}
								/>
							</div>
						);
					})
				)}
			</div>
		</div>
	);
};
